import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, distinctUntilChanged, Observable } from 'rxjs';
import { NavigationService, ObjectTools, SolBannerHandler, SolRoute, SolText } from '@sol/components';
import { SessionService } from '@asm/components-platform-context/services/session.service';

/**
 * The notice that the module the reader is looking at is in read-only mode, on every page of the app
 * that shows that module's data.
 *
 * It draws nothing. The shell's `sol-page-banner-host` renders what is published for the page, so the
 * module a page belongs to is the only thing deciding whether the notice appears — and the router
 * already carries it: every module-gated route declares `MODULE_<X>` in its authorities, so the active
 * route names the module and a page contributes no wiring of its own.
 *
 * Construct it as a field of the shell, the shape `AsmBannerStripHandler` and `SolBannerHandler` set: it
 * captures the shell's `DestroyRef` from the constructor's injection context, so its slice of the surface
 * goes with the app. The route and the session are read as two streams and combined, because either can
 * change without the other — navigating to another module's page, or the authorities arriving after it.
 */
export class AsmReadOnlyBannerHandler {
  private static readonly OWNER = 'asm-module-read-only';

  private static readonly KEY = 'module-read-only';

  private static readonly MODULE_AUTHORITY_PREFIX = 'MODULE_';

  // Dependencies
  private readonly navigationService = inject(NavigationService);
  private readonly sessionService = inject(SessionService);

  // Handlers
  private readonly solBanners: SolBannerHandler;

  constructor() {
    const destroyRef = inject(DestroyRef);
    this.solBanners = new SolBannerHandler({ owner: AsmReadOnlyBannerHandler.OWNER, level: 'page' });
    combineLatest([this.routeModules(), this.readOnlyModules()])
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(([routeModules, readOnlyModules]) => this.publish(routeModules, readOnlyModules));
  }

  /** The modules the active route is gated on, as named by its `MODULE_*` authorities. */
  private routeModules(): Observable<readonly string[]> {
    const modules$ = new BehaviorSubject<readonly string[]>(
      AsmReadOnlyBannerHandler.toModules(this.navigationService.state.activeRoute),
    );
    this.navigationService.watchComputed(
      state => AsmReadOnlyBannerHandler.toModules(state.activeRoute),
      modules => modules$.next(modules),
    );
    return modules$.pipe(distinctUntilChanged((left, right) => ObjectTools.equals(left, right)));
  }

  private readOnlyModules(): Observable<readonly string[]> {
    const modules$ = new BehaviorSubject<readonly string[]>(this.sessionService.state.readOnlyModules);
    this.sessionService.watchComputed(
      state => state.readOnlyModules,
      modules => modules$.next(modules),
    );
    return modules$.pipe(distinctUntilChanged((left, right) => ObjectTools.equals(left, right)));
  }

  private publish(routeModules: readonly string[], readOnlyModules: readonly string[]): void {
    if (!routeModules.some(module => readOnlyModules.includes(module))) {
      this.solBanners.clear();
      return;
    }
    this.solBanners.publish([
      {
        key: AsmReadOnlyBannerHandler.KEY,
        mood: 'info',
        heading: SolText.of('asm-components.read-only.title'),
        description: SolText.of('asm-components.read-only.description'),
      },
    ]);
  }

  private static toModules(route: SolRoute | undefined): readonly string[] {
    const prefix = AsmReadOnlyBannerHandler.MODULE_AUTHORITY_PREFIX;
    return (route?.authorities ?? [])
      .flat()
      .filter(authority => authority.startsWith(prefix))
      .map(authority => authority.slice(prefix.length).toLowerCase());
  }
}
